import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import FormBuilder from '../components/FormBuilder';

const emptyEvent = () => ({
  name: '', date: '', dateEnd: '', fechasFlexibles: false, sinFechas: false,
  checkInTime: '', checkOutTime: '', maxCapacity: 100, location: '', description: '',
  status: 'draft', registrationType: '', formSchema: [], excludedMandatory: [], category: 'normal', subcategory: 'general',
  requiresPayment: false, pricePerPerson: '', depositPercent: '20',
  depositRequired: false, depositAmount: '',
  singlePaymentDueDate: '',
  paymentOptions: [],
  // Backward compat (auto-generated on save):
  allowSinglePayment: true, allowInstallments: false, paymentPlans: [],
  useAgePricing: false, agePricing: null, password: '',
});

// ── Pestaña de Pagos ─────────────────────────────────────────
function PaymentTab({ form, setForm }) {
  const [addingOption, setAddingOption] = useState(false);
  const [newNumPayments, setNewNumPayments] = useState(2);
  const todayDate = new Date().toISOString().slice(0, 10);

  const basePrice = parseFloat(form.pricePerPerson) || 0;
  const depositPct = parseFloat(form.depositPercent) || 20;
  const autoDepositAmount = Math.round(basePrice * depositPct / 100 * 100) / 100;
  const currentDepositAmount = form.depositAmount !== '' && form.depositAmount !== null && form.depositAmount !== undefined
    ? parseFloat(form.depositAmount) || 0
    : autoDepositAmount;

  // Add a new payment option with evenly split installments
  const addPaymentOption = () => {
    const hasDeposit = !!form.depositRequired;
    // Si hay senial, el primer pago es la senial y se reparten numPayments-1 plazos
    // Si NO hay senial, se reparten todos los numPayments pagos
    const numInstallments = hasDeposit ? newNumPayments - 1 : newNumPayments;
    const remaining = hasDeposit ? (basePrice - currentDepositAmount) : basePrice;
    const perInstallment = Math.round(remaining / numInstallments * 100) / 100;

    const plans = [];
    for (let i = 0; i < numInstallments; i++) {
      const isLast = i === numInstallments - 1;
      const amount = isLast
        ? Math.round((remaining - perInstallment * (numInstallments - 1)) * 100) / 100
        : perInstallment;
      // Si hay senial, el primer plazo es el "2o pago"; si no, es el "1er pago"
      const paymentNumber = hasDeposit ? (i + 2) : (i + 1);
      plans.push({
        id: `plan_${Date.now()}_${i}`,
        label: paymentNumber === 1 ? '1er pago' : `${paymentNumber}o pago`,
        amount,
        dueDate: '',
      });
    }

    const option = {
      id: `opt_${Date.now()}`,
      label: `${newNumPayments} pagos`,
      numPayments: newNumPayments,
      plans,
    };

    setForm(p => ({ ...p, paymentOptions: [...(p.paymentOptions || []), option] }));
    setAddingOption(false);
    setNewNumPayments(2);
  };

  const removeOption = (optId) => setForm(p => ({
    ...p,
    paymentOptions: (p.paymentOptions || []).filter(o => o.id !== optId),
  }));

  const updateOptionPlan = (optId, planId, field, value) => {
    setForm(p => ({
      ...p,
      paymentOptions: (p.paymentOptions || []).map(opt => {
        if (opt.id !== optId) return opt;
        return {
          ...opt,
          plans: opt.plans.map(pl => {
            if (pl.id !== planId) return pl;
            return { ...pl, [field]: field === 'amount' ? parseFloat(value) || 0 : value };
          }),
        };
      }),
    }));
  };

  const updateOptionLabel = (optId, label) => {
    setForm(p => ({
      ...p,
      paymentOptions: (p.paymentOptions || []).map(opt =>
        opt.id === optId ? { ...opt, label } : opt
      ),
    }));
  };

  // Validation helper: check if plans total = pricePerPerson
  // (senial cuenta solo si depositRequired esta activado)
  const validateOption = (opt) => {
    if (!basePrice) return null;
    const installmentsTotal = opt.plans.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const total = (form.depositRequired ? currentDepositAmount : 0) + installmentsTotal;
    const diff = Math.abs(total - basePrice);
    if (diff > 0.01) return { total, diff };
    return null;
  };

  return (
    <div>
      {/* Section A: Toggle pago requerido */}
      <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.12)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1rem' }}>
        <label className="toggle-wrap">
          <input type="checkbox" checked={!!form.requiresPayment}
            onChange={e => setForm(p => ({ ...p, requiresPayment: e.target.checked }))} />
          <span className="toggle-track"><span className="toggle-thumb" /></span>
          <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Este evento requiere pago</span>
        </label>
        <p style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.4rem', marginLeft: '3rem' }}>
          Al activarlo podras configurar el precio y las opciones de pago para los inscritos.
        </p>
      </div>

      {form.requiresPayment && (
        <>
          {/* Section B: Precio */}
          <div className="form-group" style={{ marginBottom: '.8rem' }}>
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
              <input type="checkbox" checked={form.useAgePricing || false}
                onChange={e => setForm(p => ({ ...p, useAgePricing: e.target.checked }))} />
              Precio por tramos de edad
            </label>
          </div>

          {!form.useAgePricing ? (
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Precio total por persona (EUR) <span className="req">*</span></label>
                <input className="form-input" type="number" min="0" step="0.01"
                  value={form.pricePerPerson}
                  onChange={e => setForm(p => ({ ...p, pricePerPerson: e.target.value }))}
                  placeholder="150.00" />
              </div>
            </div>
          ) : (
            <div style={{ background: 'rgba(122,171,138,.08)', border: '1px solid rgba(122,171,138,.2)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '.8rem' }}>
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: '.62rem', fontWeight: 700, letterSpacing: '.12em', color: 'var(--sage)', textTransform: 'uppercase', marginBottom: '.8rem' }}>Precios por tramo de edad</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)', borderRadius: 6, border: '1px solid rgba(122,171,138,.15)' }}>
                  <span style={{ fontSize: '.85rem' }}>0 - 3 anios</span>
                  <span className="badge badge-green">Gratis</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)', borderRadius: 6, border: '1px solid rgba(122,171,138,.15)' }}>
                  <span style={{ fontSize: '.85rem', flex: 1 }}>4 - 15 anios</span>
                  <input className="form-input" type="number" min="0" step="0.01"
                    value={form.agePricing?.childPrice || ''} placeholder="0.00"
                    style={{ width: 100 }}
                    onChange={e => setForm(p => ({ ...p, agePricing: { ...p.agePricing, childPrice: e.target.value } }))} />
                  <span style={{ fontSize: '.82rem', color: 'var(--mid)' }}>EUR</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', padding: '.5rem .7rem', background: 'rgba(255,255,255,.04)', borderRadius: 6, border: '1px solid rgba(122,171,138,.15)' }}>
                  <span style={{ fontSize: '.85rem', flex: 1 }}>16+ anios</span>
                  <input className="form-input" type="number" min="0" step="0.01"
                    value={form.agePricing?.adultPrice || ''} placeholder="0.00"
                    style={{ width: 100 }}
                    onChange={e => setForm(p => ({ ...p, agePricing: { ...p.agePricing, adultPrice: e.target.value } }))} />
                  <span style={{ fontSize: '.82rem', color: 'var(--mid)' }}>EUR</span>
                </div>
              </div>
            </div>
          )}

          {/* Section C: Senial (Deposit) */}
          <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.12)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1rem' }}>
            <label className="toggle-wrap">
              <input type="checkbox" checked={!!form.depositRequired}
                onChange={e => setForm(p => ({
                  ...p,
                  depositRequired: e.target.checked,
                  depositPercent: e.target.checked ? (p.depositPercent || '20') : p.depositPercent,
                  depositAmount: e.target.checked ? '' : '',
                }))} />
              <span className="toggle-track"><span className="toggle-thumb" /></span>
              <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Requerir senial al inscribirse</span>
            </label>
            <p style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.4rem', marginLeft: '3rem' }}>
              El inscrito debera pagar una senial para confirmar su plaza.
            </p>
            {!!form.depositRequired && (
              <div style={{ marginTop: '.8rem', marginLeft: '3rem' }}>
                <div className="form-row-2" style={{ marginBottom: '.5rem' }}>
                  <div className="form-group" style={{ marginBottom: '.5rem' }}>
                    <label className="form-label">Porcentaje de senial (%)</label>
                    <input className="form-input" type="number" min="1" max="100"
                      value={form.depositPercent}
                      onChange={e => setForm(p => ({ ...p, depositPercent: e.target.value, depositAmount: '' }))}
                      style={{ maxWidth: 120 }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '.5rem' }}>
                    <label className="form-label">Importe senial (EUR)</label>
                    <input className="form-input" type="number" min="0" step="0.01"
                      value={form.depositAmount !== '' && form.depositAmount !== null && form.depositAmount !== undefined ? form.depositAmount : (basePrice ? autoDepositAmount : '')}
                      onChange={e => setForm(p => ({ ...p, depositAmount: e.target.value }))}
                      style={{ maxWidth: 140 }}
                      placeholder={basePrice ? autoDepositAmount.toFixed(2) : '0.00'} />
                    <p style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.2rem' }}>
                      Se calcula automaticamente pero puedes ajustarlo para redondear.
                    </p>
                  </div>
                </div>
                {basePrice > 0 && (
                  <p style={{ fontSize: '.78rem', color: 'var(--sage)', marginTop: '.2rem' }}>
                    Senial: {currentDepositAmount.toFixed(2)} EUR por persona
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Section D: Pago unico */}
          <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.12)', borderRadius: 'var(--radius)', padding: '1rem', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', marginBottom: '.4rem' }}>
              <span style={{ fontWeight: 600, fontSize: '.9rem' }}>Pago unico</span>
              <span className="badge badge-green" style={{ fontSize: '.6rem' }}>Siempre disponible</span>
            </div>
            <p style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.6rem' }}>
              El inscrito siempre podra pagar el importe completo de una vez.
            </p>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Fecha tope para pago unico</label>
              <input className="form-input" type="date"
                value={form.singlePaymentDueDate || ''}
                min={todayDate}
                onChange={e => setForm(p => ({ ...p, singlePaymentDueDate: e.target.value }))}
                style={{ maxWidth: 200 }} />
              <p style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.3rem' }}>
                Opcional. Si se supera esta fecha, no se podra seleccionar pago unico.
              </p>
            </div>
          </div>

          {/* Section E: Opciones de pago a plazos */}
          <div style={{ background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.12)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ padding: '.7rem 1rem', background: 'rgba(122,171,138,.08)', borderBottom: '1px solid rgba(122,171,138,.12)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '.72rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)' }}>
                Opciones de pago a plazos ({(form.paymentOptions || []).length})
              </span>
              <button className="btn btn-secondary btn-sm" onClick={() => setAddingOption(true)}
                disabled={!basePrice}>
                + Aniadir opcion de pago
              </button>
            </div>

            {!basePrice && (
              <div style={{ padding: '1rem', color: 'var(--mid)', fontSize: '.85rem', fontStyle: 'italic', textAlign: 'center' }}>
                Introduce el precio por persona para poder crear opciones de pago a plazos.
              </div>
            )}

            {basePrice > 0 && (form.paymentOptions || []).length === 0 && !addingOption && (
              <div style={{ padding: '1rem', color: 'var(--mid)', fontSize: '.85rem', fontStyle: 'italic', textAlign: 'center' }}>
                No hay opciones de pago a plazos. Aniade una para ofrecer pagos fraccionados.
                {form.depositRequired && <><br/>Si activas la senial, sera automaticamente el primer pago de cada opcion.</>}
              </div>
            )}

            {/* Adding new option prompt */}
            {addingOption && (
              <div style={{ padding: '1rem', borderBottom: '1px solid rgba(122,171,138,.1)', background: 'rgba(122,171,138,.05)' }}>
                <div style={{ fontSize: '.72rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)', marginBottom: '.6rem' }}>
                  Nueva opcion de pago
                </div>
                <div className="form-group" style={{ marginBottom: '.6rem' }}>
                  <label className="form-label">Numero total de pagos {form.depositRequired ? '(incluyendo senial)' : ''}</label>
                  <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                    <input className="form-input" type="number" min="2" max="12"
                      value={newNumPayments}
                      onChange={e => setNewNumPayments(parseInt(e.target.value) || 2)}
                      style={{ maxWidth: 100 }} />
                    <span style={{ fontSize: '.82rem', color: 'var(--mid)' }}>
                      {form.depositRequired
                        ? `(1 senial + ${Math.max(1, newNumPayments - 1)} plazo${newNumPayments - 1 > 1 ? 's' : ''})`
                        : `(${newNumPayments} plazos)`}
                    </span>
                  </div>
                </div>
                {basePrice > 0 && (
                  <p style={{ fontSize: '.78rem', color: 'var(--mid)', marginBottom: '.6rem' }}>
                    {form.depositRequired
                      ? `Senial: ${currentDepositAmount.toFixed(2)} EUR + ${Math.max(1, newNumPayments - 1)} plazos de ~${((basePrice - currentDepositAmount) / Math.max(1, newNumPayments - 1)).toFixed(2)} EUR`
                      : `${newNumPayments} plazos de ~${(basePrice / newNumPayments).toFixed(2)} EUR`}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '.5rem' }}>
                  <button className="btn btn-primary btn-sm" onClick={addPaymentOption}
                    disabled={newNumPayments < 2 || !basePrice}>
                    Crear opcion
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setAddingOption(false)}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Payment option cards */}
            {(form.paymentOptions || []).map((opt) => {
              const validation = validateOption(opt);
              return (
                <div key={opt.id} style={{ padding: '1rem', borderBottom: '1px solid rgba(122,171,138,.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                      <input className="form-input" value={opt.label}
                        onChange={e => updateOptionLabel(opt.id, e.target.value)}
                        style={{ maxWidth: 200, fontWeight: 600 }} />
                    </div>
                    <button className="btn btn-danger btn-sm" style={{ padding: '.2rem .5rem' }} onClick={() => removeOption(opt.id)}>Eliminar</button>
                  </div>

                  {/* Senial line — solo si depositRequired */}
                  {form.depositRequired && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', padding: '.4rem .7rem', background: 'rgba(232,164,48,.08)', border: '1px solid rgba(232,164,48,.15)', borderRadius: 6, marginBottom: '.4rem' }}>
                      <span style={{ fontSize: '.82rem', fontWeight: 600, color: 'var(--amber)', flex: 1 }}>1er pago — Senial (al inscribirse)</span>
                      <span style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--amber)' }}>{currentDepositAmount.toFixed(2)} EUR</span>
                    </div>
                  )}

                  {/* Installment lines */}
                  {opt.plans.map((plan, planIdx) => (
                    <div key={plan.id} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', padding: '.4rem .7rem', background: 'rgba(255,255,255,.03)', border: '1px solid rgba(122,171,138,.1)', borderRadius: 6, marginBottom: '.3rem' }}>
                      <input className="form-input" value={plan.label}
                        onChange={e => updateOptionPlan(opt.id, plan.id, 'label', e.target.value)}
                        style={{ flex: 1, maxWidth: 160 }}
                        placeholder={`Plazo ${planIdx + 1}`} />
                      <input className="form-input" type="number" min="0" step="0.01"
                        value={plan.amount}
                        onChange={e => updateOptionPlan(opt.id, plan.id, 'amount', e.target.value)}
                        style={{ width: 90 }} />
                      <span style={{ fontSize: '.8rem', color: 'var(--mid)' }}>EUR</span>
                      <input className="form-input" type="date"
                        value={plan.dueDate || ''}
                        min={todayDate}
                        onChange={e => updateOptionPlan(opt.id, plan.id, 'dueDate', e.target.value)}
                        style={{ width: 150 }} />
                    </div>
                  ))}

                  {/* Validation */}
                  {validation && (
                    <div style={{ marginTop: '.4rem', padding: '.4rem .7rem', background: 'rgba(214,60,42,.08)', border: '1px solid rgba(214,60,42,.2)', borderRadius: 6, fontSize: '.78rem', color: 'var(--danger)' }}>
                      {form.depositRequired
                        ? `La suma de senial (${currentDepositAmount.toFixed(2)}) + plazos (${opt.plans.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0).toFixed(2)}) = ${validation.total.toFixed(2)} EUR, pero el precio es ${basePrice.toFixed(2)} EUR. Diferencia: ${validation.diff.toFixed(2)} EUR.`
                        : `La suma de plazos (${opt.plans.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0).toFixed(2)}) = ${validation.total.toFixed(2)} EUR, pero el precio es ${basePrice.toFixed(2)} EUR. Diferencia: ${validation.diff.toFixed(2)} EUR.`}
                    </div>
                  )}
                  {!validation && basePrice > 0 && (
                    <div style={{ marginTop: '.4rem', fontSize: '.75rem', color: 'var(--sage)' }}>
                      Total: {basePrice.toFixed(2)} EUR {form.depositRequired ? '(senial + plazos)' : '(plazos)'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Section F: Resumen */}
          {(form.pricePerPerson || form.useAgePricing) && (
            <div style={{ background: 'rgba(74,124,89,.08)', border: '1px solid rgba(74,124,89,.2)', borderRadius: 'var(--radius)', padding: '1rem' }}>
              <div style={{ fontSize: '.65rem', fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--sage)', marginBottom: '.6rem' }}>Resumen de pagos</div>
              <div style={{ fontSize: '.85rem', display: 'flex', flexDirection: 'column', gap: '.3rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--mid)' }}>Precio por persona</span>
                  <span style={{ fontWeight: 600 }}>
                    {form.useAgePricing
                      ? `0 EUR (infante) / ${parseFloat(form.agePricing?.childPrice || 0).toFixed(2)} EUR (ninio) / ${parseFloat(form.agePricing?.adultPrice || 0).toFixed(2)} EUR (adulto)`
                      : `${parseFloat(form.pricePerPerson || 0).toFixed(2)} EUR`
                    }
                  </span>
                </div>
                {form.depositRequired && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--mid)' }}>Senial ({form.depositPercent}%)</span>
                    <span style={{ color: 'var(--amber)' }}>
                      {form.useAgePricing
                        ? 'Varia segun edad'
                        : `${currentDepositAmount.toFixed(2)} EUR`
                      }
                    </span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: 'var(--mid)' }}>Pago unico</span>
                  <span style={{ color: 'var(--sage)' }}>
                    Disponible{form.singlePaymentDueDate ? ` hasta ${new Date(form.singlePaymentDueDate).toLocaleDateString('es-ES')}` : ''}
                  </span>
                </div>
                {(form.paymentOptions || []).length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--mid)' }}>Opciones a plazos</span>
                    <span style={{ color: 'var(--sage)' }}>{form.paymentOptions.length} opcion(es)</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Modal de Evento ──────────────────────────────────────────
// fullPage: renderiza el formulario como página completa (sustituye al
// listado) en lugar de como modal superpuesto.
function EventModal({ event, onClose, onSaved, fullPage }) {
  const { token } = useAuth();
  const [tab, setTab] = useState('info');
  const todayStr = new Date().toISOString().slice(0, 10);

  const toDatetimeLocal = (date) => {
    if (!date) return '';
    if (date?.seconds) return new Date(date.seconds * 1000).toISOString().slice(0, 16);
    if (date instanceof Date) return isNaN(date) ? '' : date.toISOString().slice(0, 16);
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(date)) return date.slice(0, 16);
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      const d = new Date(date);
      return isNaN(d) ? '' : d.toISOString().slice(0, 16);
    }
    if (typeof date === 'string') {
      const m = date.match(/(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2})/);
      if (m) return `${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`;
    }
    return '';
  };

  // Extrae 'HH:mm' de una fecha guardada (para eventos antiguos sin
  // checkInTime/checkOutTime propios); medianoche se trata como "sin hora".
  const timeFrom = (date) => {
    const t = toDatetimeLocal(date).slice(11, 16);
    return t && t !== '00:00' ? t : '';
  };

  const initForm = () => {
    if (!event) return emptyEvent();
    return {
      ...emptyEvent(),
      ...event,
      date: toDatetimeLocal(event.date).slice(0, 10),
      dateEnd: toDatetimeLocal(event.dateEnd).slice(0, 10),
      fechasFlexibles: event.fechasFlexibles || false,
      sinFechas: event.sinFechas || false,
      checkInTime: event.checkInTime || timeFrom(event.date),
      checkOutTime: event.checkOutTime || timeFrom(event.dateEnd),
      requiresPayment: event.requiresPayment || false,
      pricePerPerson: event.pricePerPerson || '',
      depositRequired: event.depositRequired || false,
      depositPercent: event.depositPercent || '20',
      depositAmount: event.depositAmount ?? '',
      singlePaymentDueDate: event.singlePaymentDueDate || '',
      paymentOptions: event.paymentOptions || [],
      // Backward compat fields (read but auto-generated on save)
      allowSinglePayment: event.allowSinglePayment !== false,
      allowInstallments: event.allowInstallments || false,
      paymentPlans: event.paymentPlans || [],
      useAgePricing: event.useAgePricing || false,
      // La contraseña nunca se precarga (el servidor solo envía hasPassword);
      // vacío = mantener la actual, _removePassword = quitarla.
      password: '',
      _hasPassword: !!(event.hasPassword ?? event.password),
      _removePassword: false,
      agePricing: event.agePricing ? {
        childPrice: event.agePricing.child?.price ?? '',
        adultPrice: event.agePricing.adult?.price ?? '',
      } : null,
    };
  };

  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isNew = !event?.id;

  // Auto-cargar paquetes por defecto en eventos nuevos
  useEffect(() => {
    if (!isNew || form.formSchema.length > 0) return;
    api.request('GET', '/api/admin/question-packs', null, token)
      .then(d => {
        const defaultPacks = (d.packs || []).filter(p => p.isDefault);
        if (defaultPacks.length === 0) return;
        const defaultFields = defaultPacks.flatMap(pack =>
          (pack.fields || []).map(f => ({
            ...f,
            id: `${f.id}_${Date.now()}_${Math.random().toString(36).slice(2,5)}`,
            fromPack: pack.id,
            packName: pack.name,
          }))
        );
        if (defaultFields.length > 0) {
          setForm(p => ({ ...p, formSchema: [...p.formSchema, ...defaultFields] }));
        }
      })
      .catch(() => {});
  }, [isNew]); // eslint-disable-line

  const save = async () => {
    if (!form.name || !form.registrationType) {
      setError('Nombre y tipo de inscripción son obligatorios'); return;
    }
    if (!form.sinFechas && !form.date) {
      setError('Indica la fecha de inicio (o elige "Sin fechas")'); return;
    }
    if (!form.sinFechas && form.fechasFlexibles && !form.dateEnd) {
      setError('Un evento con fechas flexibles necesita fecha de fin del periodo'); return;
    }
    if (!form.sinFechas && form.dateEnd && form.dateEnd < form.date) {
      setError('La fecha de fin no puede ser anterior a la de inicio'); return;
    }
    if (form.requiresPayment && !form.useAgePricing && !form.pricePerPerson) {
      setError('Indica el precio por persona'); return;
    }
    setSaving(true);
    try {
      const basePrice = parseFloat(form.pricePerPerson) || 0;
      const depositPct = parseFloat(form.depositPercent) || 20;
      const autoDeposit = Math.round(basePrice * depositPct / 100 * 100) / 100;
      const finalDepositAmount = form.depositAmount !== '' && form.depositAmount !== null && form.depositAmount !== undefined
        ? parseFloat(form.depositAmount) || 0
        : autoDeposit;

      const payload = {
        ...form,
        // La fecha se combina con la hora de entrada/salida elegida
        date: form.sinFechas ? null : new Date(`${form.date}T${form.checkInTime || '00:00'}`).toISOString(),
        sinFechas: !!form.sinFechas,
        fechasFlexibles: !form.sinFechas && !!form.fechasFlexibles,
        checkInTime: form.checkInTime || null,
        checkOutTime: form.checkOutTime || null,
        pricePerPerson: form.requiresPayment ? basePrice : 0,
        depositRequired: form.requiresPayment ? (form.depositRequired || false) : false,
        depositPercent: form.requiresPayment && form.depositRequired ? depositPct : 0,
        depositAmount: form.requiresPayment && form.depositRequired ? finalDepositAmount : null,
        singlePaymentDueDate: form.singlePaymentDueDate || null,
        paymentOptions: form.requiresPayment ? (form.paymentOptions || []) : [],
        // Backward compat fields (auto-generated):
        allowSinglePayment: true,
        allowInstallments: (form.paymentOptions || []).length > 0,
        paymentPlans: (form.paymentOptions || []).length > 0 ? (form.paymentOptions[0]?.plans || []) : [],
      };
      payload.category = form.category || 'normal';
      // Contraseña: solo se envía si se escribe una nueva o se quita explícitamente;
      // si el campo queda vacío, el evento conserva la que tenga.
      if (form._removePassword) payload.password = null;
      else if (form.password?.trim()) payload.password = form.password.trim();
      else delete payload.password;
      delete payload._hasPassword;
      delete payload._removePassword;
      if (form.sinFechas || !form.dateEnd) payload.dateEnd = null;
      else payload.dateEnd = new Date(`${form.dateEnd}T${form.checkOutTime || '00:00'}`).toISOString();
      if (isNew) await api.createEvent(token, payload);
      else await api.updateEvent(token, event.id, payload);
      onSaved();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  // Step 1: elegir tipo si es nuevo
  if (isNew && !form.registrationType) {
    return (
      <div className={fullPage ? 'main-content page-enter' : 'modal-overlay'}
        onClick={e => !fullPage && e.target === e.currentTarget && onClose()}>
        <div className={fullPage ? 'card' : 'modal'} style={fullPage ? { maxWidth: 640, margin: '0 auto', padding: '1.6rem' } : { maxWidth: 560 }}>
          <div className="modal-header">
            <div><div className="section-label">Nuevo evento</div><h2>¿Cómo es la inscripción?</h2></div>
            {fullPage
              ? <button className="btn btn-ghost btn-sm" onClick={onClose}>← Volver</button>
              : <button className="modal-close" onClick={onClose}>✕</button>}
          </div>
          <p style={{ color: 'var(--mid)', fontSize: '.92rem', marginBottom: '1.4rem', lineHeight: 1.6 }}>
            El tipo de inscripción determina cómo podrán registrarse los usuarios. No se puede cambiar una vez el evento tenga inscritos.
          </p>
          <div style={{ display: 'flex', gap: '.9rem', marginBottom: '1.4rem' }}>
            {[
              { value: 'individual', icon: '👤', title: 'Individual', desc: 'Cada persona rellena su propio formulario. Solo se permite una inscripción por usuario en este evento.' },
              { value: 'group', icon: '👨‍👩‍👧', title: 'Grupal', desc: 'Un usuario puede inscribir a varias personas en una misma reserva. Ideal para eventos familiares.' },
              { value: 'school', icon: '🏫', title: 'Colegios', desc: 'Inscripción para grupos escolares. Funciona como inscripción individual. El nombre del centro se mostrará de forma anónima.' },
            ].map(t => (
              <button key={t.value}
                style={{ flex: 1, padding: '1.4rem 1rem', border: '2px solid rgba(122,171,138,.2)', borderRadius: 'var(--radius)', background: 'rgba(255,255,255,.04)', cursor: 'pointer', textAlign: 'center', fontFamily: "'DM Sans',sans-serif", transition: 'all .22s' }}
                onClick={() => setForm(p => ({
                  ...p,
                  registrationType: t.value === 'school' ? 'individual' : t.value,
                  category: t.value === 'school' ? 'school' : 'normal',
                }))}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--sage)'; e.currentTarget.style.background = 'rgba(122,171,138,.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(122,171,138,.2)'; e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
              >
                <div style={{ fontSize: '2rem', marginBottom: '.6rem' }}>{t.icon}</div>
                <div style={{ fontSize: '.78rem', fontWeight: 700, letterSpacing: '.1em', color: 'var(--sage)', textTransform: 'uppercase', marginBottom: '.3rem' }}>{t.title}</div>
                <div style={{ fontSize: '.8rem', color: 'var(--mid)', lineHeight: 1.5 }}>{t.desc}</div>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={fullPage ? 'main-content page-enter' : 'modal-overlay'}
      onClick={e => !fullPage && e.target === e.currentTarget && onClose()}>
      <div className={fullPage ? 'card' : 'modal modal-lg'}
        style={fullPage ? { maxWidth: 960, margin: '0 auto', padding: '1.6rem' } : undefined}>
        <div className="modal-header">
          <div>
            <div className="section-label">{isNew ? 'Nuevo evento' : 'Editar evento'} · {form.category === 'school' ? 'Colegios' : form.registrationType === 'individual' ? 'Individual' : 'Grupal'}</div>
            <h2>{isNew ? 'Crear evento' : form.name}</h2>
          </div>
          {fullPage
            ? <button className="btn btn-ghost btn-sm" onClick={onClose}>← Volver</button>
            : <button className="modal-close" onClick={onClose}>✕</button>}
        </div>

        {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

        <div className="tabs">
          <button className={`tab ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>🏰 Información</button>
          <button className={`tab ${tab === 'form' ? 'active' : ''}`} onClick={() => setTab('form')}>
            📋 Formulario
            {form.formSchema.length > 0 && (
              <span style={{ background: 'var(--fern)', color: '#fff', fontSize: '.55rem', padding: '.1rem .45rem', borderRadius: 8, marginLeft: '.3rem', fontWeight: 700 }}>
                {form.formSchema.length}
              </span>
            )}
          </button>
          <button className={`tab ${tab === 'payment' ? 'active' : ''}`} onClick={() => setTab('payment')}>
            💳 Pago
            {form.requiresPayment && (
              <span style={{ background: 'var(--amber)', color: 'var(--forest)', fontSize: '.55rem', padding: '.1rem .45rem', borderRadius: 8, marginLeft: '.3rem', fontWeight: 700 }}>
                {form.pricePerPerson ? `${form.pricePerPerson}€` : '·'}
              </span>
            )}
          </button>
        </div>

        {tab === 'info' && (
          <div>
            <div className="form-group">
              <label className="form-label">Nombre del evento <span className="req">*</span></label>
              <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Fechas del evento</label>
              <div style={{ display: 'flex', gap: '.6rem' }}>
                {[
                  { key: 'fijas', icon: '📌', title: 'Fechas fijas' },
                  { key: 'flexibles', icon: '🗓', title: 'Periodo flexible' },
                  { key: 'sin', icon: '🔓', title: 'Sin fechas' },
                ].map(m => {
                  const active = form.sinFechas ? m.key === 'sin' : form.fechasFlexibles ? m.key === 'flexibles' : m.key === 'fijas';
                  return (
                    <button key={m.key} type="button"
                      onClick={() => setForm(p => ({ ...p, fechasFlexibles: m.key === 'flexibles', sinFechas: m.key === 'sin' }))}
                      style={{
                        flex: 1, padding: '.65rem .5rem', borderRadius: 'var(--radius)', cursor: 'pointer',
                        textAlign: 'center', fontFamily: "'DM Sans',sans-serif", transition: 'all .18s',
                        border: active ? '2px solid var(--sage)' : '2px solid rgba(122,171,138,.2)',
                        background: active ? 'rgba(122,171,138,.12)' : 'rgba(255,255,255,.04)',
                      }}>
                      <div style={{ fontSize: '1.05rem' }}>{m.icon}</div>
                      <div style={{ fontSize: '.72rem', fontWeight: 700, color: active ? 'var(--sage)' : 'var(--mid)' }}>{m.title}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: '.78rem', color: 'var(--mid)', marginTop: '.35rem', fontStyle: 'italic' }}>
                {form.sinFechas
                  ? 'El evento no tiene fechas: cada familia indicará su fecha de entrada y de salida al inscribirse.'
                  : form.fechasFlexibles
                    ? 'Ideal para registro abierto (p.ej. agosto): al inscribirse, cada familia indicará su fecha de llegada y de salida dentro del periodo que definas abajo.'
                    : 'El evento se celebra en las fechas que definas abajo.'}
              </div>
            </div>
            {!form.sinFechas && (
              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">{form.fechasFlexibles ? 'Inicio del periodo' : 'Fecha de inicio'} <span className="req">*</span></label>
                  <input className="form-input" type="date" value={form.date}
                    min={todayStr}
                    onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">{form.fechasFlexibles ? 'Fin del periodo' : 'Fecha de fin'} {form.fechasFlexibles && <span className="req">*</span>}</label>
                  <input className="form-input" type="date" value={form.dateEnd || ''}
                    min={form.date || todayStr}
                    onChange={e => setForm(p => ({ ...p, dateEnd: e.target.value }))} />
                </div>
              </div>
            )}
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">🕓 Hora de entrada</label>
                <input className="form-input" type="time" value={form.checkInTime}
                  onChange={e => setForm(p => ({ ...p, checkInTime: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">🕓 Hora de salida</label>
                <input className="form-input" type="time" value={form.checkOutTime}
                  onChange={e => setForm(p => ({ ...p, checkOutTime: e.target.value }))} />
              </div>
            </div>
            <div style={{ fontSize: '.75rem', color: 'var(--mid)', marginTop: '-.5rem', marginBottom: '1rem', fontStyle: 'italic' }}>
              Opcional: los inscritos verán estas horas como hora de entrada y de salida.
            </div>
            <div className="form-group">
              <label className="form-label">Capacidad máxima <span className="req">*</span></label>
              <input className="form-input" type="number" min="1" value={form.maxCapacity}
                onChange={e => setForm(p => ({ ...p, maxCapacity: parseInt(e.target.value) }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Ubicación</label>
              <input className="form-input" value={form.location}
                onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                placeholder="Aldea Central, Catedral" />
            </div>
            <div className="form-group">
              <label className="form-label">Descripción</label>
              <textarea className="form-textarea" value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Categoría</label>
                <select className="form-select" value={form.category || 'normal'}
                  onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                  <option value="normal">🏕 Evento normal</option>
                  <option value="school">🏫 Inscripción colegio</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.status}
                  onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="active">Activo — reservas abiertas</option>
                  <option value="draft">Borrador — no visible</option>
                  <option value="closed">Cerrado — sin nuevas reservas</option>
                  <option value="archived">Archivado — oculto</option>
                </select>
              </div>
            </div>

            {form.category !== 'school' && (
              <div className="form-group">
                <label className="form-label">Tipo de evento</label>
                <select className="form-select" value={form.subcategory || 'general'}
                  onChange={e => setForm(p => ({ ...p, subcategory: e.target.value }))}>
                  <option value="general">🏕 Evento general</option>
                  <option value="campamento_verano">☀️ Campamento de verano</option>
                  <option value="campamento_familiar">👨‍👩‍👧 Campamento familiar</option>
                </select>
                <p style={{ fontSize: '.72rem', color: 'var(--mid)', marginTop: '.3rem' }}>
                  Determina en que seccion aparece en la pagina de inicio del cliente.
                </p>
              </div>
            )}
            {!isNew && form.category === 'school' && event?.schoolId && (
              <div className="alert alert-info" style={{ marginTop: '.5rem', marginBottom: '.5rem' }}>
                <span>🏫</span> ID anónimo para familias: <strong>{event.schoolId}</strong>
              </div>
            )}
            <div className="form-group" style={{ marginTop: '.5rem' }}>
              <label className="form-label">Contraseña de acceso</label>
              <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
                <input className="form-input" value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value, _removePassword: false }))}
                  placeholder={form._hasPassword && !form._removePassword
                    ? 'El evento ya tiene contraseña — escribe una nueva solo si quieres cambiarla'
                    : 'Dejar vacío para acceso libre'} />
                {(form.password || (form._hasPassword && !form._removePassword)) && (
                  <span className="badge badge-amber" style={{ whiteSpace: 'nowrap' }}>🔒 Protegido</span>
                )}
                {form._hasPassword && !form.password && (
                  <button type="button" className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }}
                    onClick={() => setForm(p => ({ ...p, _removePassword: !p._removePassword }))}>
                    {form._removePassword ? '↩ Mantenerla' : '✕ Quitarla'}
                  </button>
                )}
              </div>
              <p style={{ fontSize: '.75rem', color: 'var(--mid)', marginTop: '.3rem' }}>
                {form._removePassword
                  ? 'Al guardar, el evento quedará sin contraseña (acceso libre).'
                  : form._hasPassword
                    ? 'Por seguridad la contraseña actual no se muestra. Déjalo vacío para mantenerla.'
                    : 'Si estableces una contraseña, los usuarios deberán introducirla para inscribirse en este evento'}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '.6rem' }}>
              <button className="btn btn-secondary" onClick={() => setTab('form')}>Siguiente: Formulario →</button>
            </div>
          </div>
        )}

        {tab === 'form' && (
          <div>
            <FormBuilder schema={form.formSchema}
              onChange={s => setForm(p => ({ ...p, formSchema: s }))}
              registrationType={form.registrationType}
              excludedMandatory={form.excludedMandatory || []}
              onExcludedMandatoryChange={arr => setForm(p => ({ ...p, excludedMandatory: arr }))} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '.5rem' }}>
              <button className="btn btn-ghost" onClick={() => setTab('info')}>← Información</button>
              <button className="btn btn-secondary" onClick={() => setTab('payment')}>Siguiente: Pago →</button>
            </div>
          </div>
        )}

        {tab === 'payment' && (
          <div>
            <PaymentTab form={form} setForm={setForm} />
            <div style={{ marginTop: '.5rem' }}>
              <button className="btn btn-ghost" onClick={() => setTab('form')}>← Formulario</button>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '.8rem', justifyContent: 'flex-end', paddingTop: '1rem', borderTop: 'var(--border)', marginTop: '.8rem' }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <><span className="spinner" /> Guardando...</> : 'Guardar evento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lista de Eventos ─────────────────────────────────────────
export default function AdminEvents() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [campistas, setCampistas] = useState([]);
  const [loadingCamp, setLoadingCamp] = useState(false);
  const [campistaSel, setCampistaSel] = useState(null);

  const load = () => {
    setLoading(true);
    api.getAdminEvents(token)
      .then(d => setEvents(d.events || []))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, [token]);

  const onSaved = () => { setEditing(null); setCreating(false); load(); };

  const handleArchive = async (ev) => {
    try {
      await api.updateEvent(token, ev.id, { status: 'archived' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleRestore = async (ev) => {
    try {
      await api.updateEvent(token, ev.id, { status: 'closed' });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.deleteEvent(token, confirmDelete.id);
      setConfirmDelete(null);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const abrirDetalle = (ev) => {
    setDetalle(ev);
    setCampistaSel(null);
    setLoadingCamp(true);
    api.getEventCampistas(token, ev.id)
      .then(r => setCampistas(r.campistas || []))
      .catch(() => setCampistas([]))
      .finally(() => setLoadingCamp(false));
  };

  const cerrarDetalle = () => { setDetalle(null); setCampistas([]); setCampistaSel(null); };

  const campistaNombre = (c) => {
    if (c.name) return c.name;
    const partes = [c.firstName, c.lastName1, c.lastName2].filter(Boolean).join(' ').trim();
    return partes || '(sin datos)';
  };

  const sourceBadge = (src) => {
    if (src === 'web') return { icon: '🌐', label: 'Web', cls: 'badge-amber' };
    if (src === 'manual') return { icon: '🏦', label: 'Manual', cls: 'badge-stone' };
    return { icon: '🏠', label: 'Nativa', cls: 'badge-green' };
  };

  const visibleEvents = showArchived ? events : events.filter(e => e.status !== 'archived');
  const archivedCount = events.filter(e => e.status === 'archived').length;

  if (loading) return (
    <div className="loading-screen" style={{ minHeight: 'auto', padding: '3rem' }}>
      <div className="spinner spinner-lg" />
    </div>
  );

  // Crear/editar evento: página completa en lugar de modal
  if (creating) return <EventModal fullPage onClose={() => setCreating(false)} onSaved={onSaved} />;
  if (editing) return <EventModal fullPage event={editing} onClose={() => setEditing(null)} onSaved={onSaved} />;

  return (
    <div className="main-content page-enter">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1.8rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="section-label">Administración</div>
          <h1>Eventos</h1>
          <p style={{ color: 'var(--mid)', fontStyle: 'italic', fontSize: '.95rem', marginTop: '.2rem' }}>
            Crea eventos con formularios de inscripción personalizados
          </p>
        </div>
        <div style={{ display: 'flex', gap: '.6rem', alignItems: 'center' }}>
          {archivedCount > 0 && (
            <button
              className={`btn ${showArchived ? 'btn-secondary' : 'btn-ghost'} btn-sm`}
              onClick={() => setShowArchived(v => !v)}
              style={{ fontSize: '.82rem' }}
            >
              {showArchived ? '🗄 Ocultar archivados' : `🗄 Archivados (${archivedCount})`}
            </button>
          )}
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Nuevo evento</button>
        </div>
      </div>

      {error && <div className="alert alert-error"><span>⚠</span> {error}</div>}

      {visibleEvents.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--mid)' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '.5rem' }}>No hay eventos {showArchived ? '' : 'activos'}</p>
          <p style={{ fontSize: '.85rem', fontStyle: 'italic' }}>Crea un nuevo evento para empezar</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: '1rem' }}>
        {visibleEvents.map(ev => {
          // Ocupación = inscritos nativos (currentCount) + reservas de campamento web (externalCount).
          const ocupados = (ev.currentCount || 0) + (ev.externalCount || 0);
          const pct = Math.min(100, Math.round((ocupados / ev.maxCapacity) * 100));
          const isArchived = ev.status === 'archived';
          return (
            <div className="card" key={ev.id} onClick={() => abrirDetalle(ev)}
              title="Ver inscripciones"
              style={{ display: 'flex', flexDirection: 'column', gap: '.65rem', opacity: isArchived ? 0.6 : 1, cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '.6rem' }}>
                <div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: '.6rem', fontWeight: 700, letterSpacing: '.15em', color: 'var(--sage)', textTransform: 'uppercase' }}>
                    {ev.sinFechas || !ev.date
                      ? '🔓 Sin fechas · las eligen los inscritos'
                      : <>
                          {new Date(ev.date).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' })}
                          {ev.dateEnd && ` → ${new Date(ev.dateEnd).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`}
                        </>}
                    {(ev.checkInTime || ev.checkOutTime) && ` · 🕓 ${ev.checkInTime || '—'}–${ev.checkOutTime || '—'}`}
                  </div>
                  <div style={{ fontFamily: "'Fraunces',serif", fontSize: '1rem', fontWeight: 700, color: 'var(--white)', margin: '.2rem 0' }}>{ev.name}</div>
                  {ev.location && <div style={{ fontSize: '.8rem', color: 'var(--sage)' }}>📍 {ev.location}</div>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '.3rem' }}>
                  <span className={`badge ${ev.status === 'active' ? 'badge-green' : ev.status === 'closed' ? 'badge-danger' : isArchived ? 'badge-stone' : 'badge-stone'}`}
                    style={isArchived ? { background: 'rgba(120,120,120,.15)', color: '#888' } : undefined}>
                    {ev.status === 'active' ? 'Activo' : ev.status === 'closed' ? 'Cerrado' : isArchived ? '🗄 Archivado' : 'Borrador'}
                  </span>
                  {ev.requiresPayment && (
                    <span className="badge badge-amber">💳 {ev.pricePerPerson}€</span>
                  )}
                  {(ev.hasPassword ?? ev.password) && (
                    <span className="badge" style={{ background: 'rgba(245,158,11,.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,.25)', fontSize: '.65rem' }}>
                      🔒
                    </span>
                  )}
                </div>
              </div>
              {ev.description && <p style={{ fontSize: '.85rem', color: 'var(--mid)', fontStyle: 'italic', lineHeight: 1.55 }}>{ev.description}</p>}
              <div className="cap-wrap">
                <div className="cap-track"><div className="cap-fill" style={{ width: `${pct}%` }} /></div>
                <div className="cap-info">
                  <span>{ocupados} / {ev.maxCapacity}</span>
                  <span>{Math.max(0, ev.maxCapacity - ocupados)} plazas</span>
                </div>
              </div>
              <div style={{ fontSize: '.72rem', color: 'var(--sage)', fontWeight: 600 }}>Ver inscripciones →</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '.5rem', marginTop: 'auto' }}>
                <div style={{ display: 'flex', gap: '.3rem', flexWrap: 'wrap' }}>
                  <span className={`badge ${ev.registrationType === 'individual' ? 'badge-indiv' : 'badge-group'}`}>
                    {ev.registrationType === 'individual' ? 'Individual' : 'Grupal'}
                  </span>
                  {ev.category === 'school' && ev.schoolId && (
                    <span className="badge" style={{ background: 'rgba(74,143,168,.15)', color: '#4a8fa8', border: '1px solid rgba(74,143,168,.25)' }}>
                      🏫 ID: {ev.schoolId}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '.4rem' }}>
                  {isArchived ? (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); handleRestore(ev); }}
                        title="Restaurar evento" style={{ fontSize: '.78rem' }}>
                        ↩ Restaurar
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); setConfirmDelete(ev); }}
                        title="Eliminar permanentemente" style={{ fontSize: '.78rem', padding: '.25rem .5rem' }}>
                        🗑
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); handleArchive(ev); }}
                        title="Archivar evento" style={{ fontSize: '.78rem', padding: '.25rem .5rem' }}>
                        🗄
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); setEditing(ev); }}>Editar</button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal confirmación de eliminación */}
      {confirmDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && !deleting && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <div>
                <div className="section-label" style={{ color: '#e55' }}>Acción irreversible</div>
                <h2>Eliminar evento</h2>
              </div>
              <button className="modal-close" onClick={() => !deleting && setConfirmDelete(null)}>✕</button>
            </div>
            <p style={{ color: 'var(--mid)', fontSize: '.92rem', lineHeight: 1.6, marginBottom: '1rem' }}>
              ¿Estás seguro de que quieres eliminar <strong style={{ color: 'var(--white)' }}>{confirmDelete.name}</strong>?
              {(confirmDelete.currentCount > 0) && (
                <span style={{ display: 'block', marginTop: '.5rem', color: '#e55', fontWeight: 600 }}>
                  ⚠ Este evento tiene {confirmDelete.currentCount} inscrito(s). Se perderán todas las reservas asociadas.
                </span>
              )}
            </p>
            <p style={{ fontSize: '.82rem', color: 'var(--mid)', fontStyle: 'italic', marginBottom: '1.2rem' }}>
              Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: '.6rem', justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancelar</button>
              <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? <><span className="spinner" /> Eliminando...</> : '🗑 Eliminar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de inscripciones (campistas) */}
      {detalle && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && cerrarDetalle()}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="section-label">Inscripciones ({campistas.length})</div>
                <h2>{detalle.name}</h2>
              </div>
              <button className="modal-close" onClick={cerrarDetalle}>✕</button>
            </div>

            {loadingCamp && (
              <div style={{ textAlign: 'center', padding: '2.5rem' }}>
                <span className="spinner spinner-lg" />
              </div>
            )}

            {!loadingCamp && campistas.length === 0 && (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: 'var(--mid)' }}>
                <p style={{ fontSize: '1rem', marginBottom: '.4rem' }}>No hay inscripciones en este evento</p>
                <p style={{ fontSize: '.85rem', fontStyle: 'italic' }}>Cuando alguien se inscriba aparecerá aquí</p>
              </div>
            )}

            {/* Lista de campistas */}
            {!loadingCamp && campistas.length > 0 && !campistaSel && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                {campistas.map((c, idx) => {
                  const sb = sourceBadge(c._source);
                  const edad = c.age ? `${c.age} años` : (c.birthYear ? `${c.birthYear}` : '');
                  return (
                    <div key={c._reservaId ? `${c._reservaId}_${idx}` : idx}
                      className="member-box"
                      onClick={() => setCampistaSel(c)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.7rem' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '.15rem' }}>
                        <span style={{ fontWeight: 600, color: c._datosPendientes ? 'var(--mid)' : 'var(--white)' }}>
                          {c._datosPendientes ? '(datos pendientes)' : campistaNombre(c)}
                        </span>
                        {edad && <span style={{ fontSize: '.78rem', color: 'var(--mid)' }}>{edad}</span>}
                        {c._inscritoAt && (
                          <span style={{ fontSize: '.72rem', color: 'var(--sage)' }}>
                            ✍ Inscrito el {new Date(c._inscritoAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <span className={`badge ${sb.cls}`} style={{ whiteSpace: 'nowrap' }}>{sb.icon} {sb.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Detalle de un campista */}
            {!loadingCamp && campistaSel && (() => {
              const c = campistaSel;
              const sb = sourceBadge(c._source);
              const Row = ({ label, value }) => (value !== undefined && value !== null && value !== '') ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '.3rem 0', borderBottom: '1px solid rgba(122,171,138,.08)' }}>
                  <span style={{ color: 'var(--mid)', fontSize: '.82rem' }}>{label}</span>
                  <span style={{ color: 'var(--white)', fontSize: '.85rem', textAlign: 'right', fontWeight: 500 }}>{String(value)}</span>
                </div>
              ) : null;
              const Bool = ({ label, value }) => (value !== undefined && value !== null) ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '.3rem 0', borderBottom: '1px solid rgba(122,171,138,.08)' }}>
                  <span style={{ color: 'var(--mid)', fontSize: '.82rem' }}>{label}</span>
                  <span style={{ fontSize: '.85rem' }}>{value ? '✅' : '❌'}</span>
                </div>
              ) : null;
              const Group = ({ title, children }) => {
                const hasContent = (Array.isArray(children) ? children : [children]).some(Boolean);
                if (!hasContent) return null;
                return (
                  <div style={{ marginBottom: '1rem' }}>
                    <div className="section-label" style={{ marginBottom: '.4rem' }}>{title}</div>
                    <div>{children}</div>
                  </div>
                );
              };
              const emergencia = [c.contactoEmergenciaNombre, c.contactoEmergenciaTelefono, c.contactoEmergenciaRelacion].filter(Boolean).join(' · ');
              return (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.7rem', marginBottom: '1rem' }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setCampistaSel(null)}>← Volver a la lista</button>
                    <span className={`badge ${sb.cls}`}>{sb.icon} {sb.label}</span>
                  </div>

                  <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: '1.15rem', color: 'var(--white)', marginBottom: '1rem' }}>
                    {c._datosPendientes ? '(datos pendientes)' : campistaNombre(c)}
                  </h3>

                  <Group title="Identidad">
                    <Row label="Nombre" value={c.name || c.firstName} />
                    <Row label="Primer apellido" value={c.lastName1} />
                    <Row label="Segundo apellido" value={c.lastName2} />
                    <Row label="Año nacimiento" value={c.birthYear} />
                    <Row label="Edad" value={c.age} />
                    <Row label="Sexo" value={c.sex} />
                    <Row label="Tipo documento" value={c.docType} />
                    <Row label="Nº documento" value={c.docNum} />
                  </Group>

                  <Group title="Contacto y domicilio">
                    <Row label="Email" value={c.email} />
                    <Row label="Teléfono" value={c.phone} />
                    <Row label="Dirección" value={c.address} />
                    <Row label="Municipio" value={c.municipality} />
                    <Row label="Provincia" value={c.province} />
                    <Row label="País" value={c.country} />
                  </Group>

                  <Group title="Tutor">
                    <Row label="Nombre tutor" value={c.tutorName} />
                    <Row label="Teléfono tutor" value={c.tutorPhone} />
                    <Row label="Email tutor" value={c.tutorEmail} />
                  </Group>

                  <Group title="Salud">
                    <Row label="Alergias" value={c.alergias} />
                    <Row label="Medicación actual" value={c.medicacionActual} />
                    <Row label="Enfermedades" value={c.enfermedades} />
                    <Row label="Grupo sanguíneo" value={c.grupoSanguineo} />
                    <Row label="Tarjeta sanitaria" value={c.tarjetaSanitaria} />
                    <Row label="Contacto emergencia" value={emergencia} />
                    <Row label="Notas dietéticas" value={c.dietaryNotes} />
                  </Group>

                  <Group title="Autorizaciones">
                    <Bool label="Cesión de datos" value={c.autorizaCesionDatos} />
                    <Bool label="Imagen en redes" value={c.autorizaImagenRedes} />
                    <Bool label="Administrar medicación" value={c.autorizaMedicacion} />
                    <Bool label="Comunicaciones" value={c.autorizaComunicaciones} />
                  </Group>

                  <Group title="Reserva">
                    <Row label="Origen" value={`${sb.icon} ${sb.label}`} />
                    <Row label="Fecha de inscripción" value={c._inscritoAt ? new Date(c._inscritoAt).toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : undefined} />
                    <Row label="Contacto reserva" value={c._contactoNombre} />
                    <Row label="Email contacto" value={c._contactoEmail} />
                    <Row label="ID reserva" value={c._reservaId} />
                    <Row label="Transporte (bus)" value={c.busTipo} />
                  </Group>
                </div>
              );
            })()}
          </div>
        </div>
      )}

    </div>
  );
}
